import React from 'react';
import { Modal } from 'antd';
import adminAxios from './adminAxios';
import { auth } from '../firebaseAuth';

type Confirmation = { confirmationId: string; digest: string; recipientCount: number; recipients: string[]; expiresAt: string };

/** Retain the exact confirmation across a lost HTTP response or page reload. */
export async function confirmedCommunication<T = any>(url: string, payload: Record<string, unknown>): Promise<{ data: T }> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify({ url, payload })));
  const key = `communication:${auth.currentUser?.uid}:${Array.from(new Uint8Array(bytes), value => value.toString(16).padStart(2, '0')).join('')}`;
  const stored = sessionStorage.getItem(key);
  let confirmation: Confirmation;
  if (stored) confirmation = JSON.parse(stored);
  else {
    const preview = await adminAxios.post(url, payload);
    if (preview.data.status !== 'preview') return preview;
    confirmation = preview.data;
    sessionStorage.setItem(key, JSON.stringify(confirmation));
  }
  const accepted = await new Promise<boolean>(resolve => Modal.confirm({
    title: `Send ${confirmation.recipientCount} email${confirmation.recipientCount === 1 ? '' : 's'}?`,
    content: <div><p>These exact recipients and message contents are frozen for this confirmation.</p>
      <ul>{confirmation.recipients.map((email, index) => <li key={index}>{email}</li>)}</ul>
      <p>Expires {new Date(confirmation.expiresAt).toLocaleTimeString()}. If delivery needs reconciliation, keep operation {confirmation.confirmationId} and resolve it before creating another send.</p></div>,
    okText: 'Confirm send', cancelText: 'Cancel', onOk: () => resolve(true), onCancel: () => resolve(false),
  }));
  if (!accepted) throw new Error('Email send cancelled. The saved preview can be confirmed later.');
  try {
    const result = await adminAxios.post<T>(url, { ...payload, confirmationId: confirmation.confirmationId, digest: confirmation.digest });
    sessionStorage.removeItem(key);
    return result;
  } catch (failure: any) {
    if (failure?.response?.status === 409 && String(failure.response.data?.detail).includes('expired')) sessionStorage.removeItem(key);
    throw failure;
  }
}
